import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const PUBLISHER_JWT_KEY = 'publisher_jwt';
const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const getToken = () => localStorage.getItem(PUBLISHER_JWT_KEY) || '';

const QUILL_MODULES = { toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link', 'clean']] };
const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

// Default body — mirrors the backend fallback (design 040). Publisher can edit/override.
const DEFAULT_SUBJECT = "You've earned another free month of {{subscription_name}}";
const DEFAULT_HTML =
  '<p>Because you fully paid down your points balance last month, you now have another full month of {{subscription_name}} for free!</p>' +
  '<p>To keep your access after this month, go to https://app.vusub.ai/ and pay down your new balance. You can do this at any point in the month to keep your free access rolling.</p>' +
  '<p>Thanks for supporting {{subscription_name}}, {{publisher_name}}, and independent journalism!</p>' +
  '<p>Best,<br>The VuSub Team</p>';

// ─── Styled ───────────────────────────────────────────────────────────────────
const Wrap = styled.div`color: white;`;
const Title = styled.h1`
  font-size: 1.35rem; font-weight: 700; margin: 0 0 4px;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const SubTitle = styled.p`font-size: 0.82rem; color: rgba(255,255,255,0.4); margin: 0 0 20px;`;
const Hint = styled.div`
  background: rgba(101,194,213,0.08); border: 1px solid rgba(101,194,213,0.2);
  color: rgba(180,220,230,0.9); border-radius: 10px; padding: 10px 14px;
  font-size: 0.82rem; margin-bottom: 20px;
`;
const Row = styled.div`display: flex; gap: 12px; align-items: center; flex-wrap: wrap; margin-bottom: 18px;`;
const Select = styled.select`
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px;
  color: white; font-size: 0.9rem; padding: 8px 12px; outline: none; cursor: pointer;
  &:focus { border-color: rgba(255,71,87,0.5); } option { background: #1a1a2e; }
`;
const Section = styled.div`
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px; padding: 20px 22px; margin-bottom: 20px;
`;
const SectionTitle = styled.div`
  font-size: 0.78rem; font-weight: 600; color: rgba(255,255,255,0.4);
  text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 14px;
`;
const Label = styled.label`display: block; font-size: 0.75rem; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.05em; margin: 12px 0 6px;`;
const Input = styled.input`
  width: 100%; box-sizing: border-box; background: #10101c; border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px; color: white; font-size: 0.9rem; padding: 10px 12px; outline: none;
  &:focus { border-color: #ff4757; }
`;
const Btn = styled.button`
  background: ${p => p.$primary ? '#ff4757' : 'rgba(255,255,255,0.06)'};
  color: ${p => p.$primary ? 'white' : 'rgba(255,255,255,0.8)'};
  border: 1px solid ${p => p.$primary ? 'transparent' : 'rgba(255,255,255,0.15)'};
  border-radius: 10px; font-size: 0.88rem; font-weight: 600; padding: 9px 18px;
  cursor: pointer; white-space: nowrap;
  &:hover:not(:disabled) { opacity: 0.88; }
  &:disabled { opacity: 0.4; cursor: default; }
`;
const SmallBtn = styled.button`
  background: none; border: 1px solid rgba(255,255,255,0.15); border-radius: 7px;
  color: rgba(255,255,255,0.6); font-size: 0.78rem; padding: 4px 10px; cursor: pointer;
  &:hover { border-color: rgba(255,255,255,0.35); color: white; }
`;
const Toggle = styled.label`display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; color: rgba(255,255,255,0.8); cursor: pointer;`;
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 0.85rem;`;
const Th = styled.th`text-align: left; padding: 8px 10px; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: rgba(255,255,255,0.35); border-bottom: 1px solid rgba(255,255,255,0.06);`;
const Td = styled.td`padding: 9px 10px; color: rgba(255,255,255,0.78); border-bottom: 1px solid rgba(255,255,255,0.04); vertical-align: middle;`;
const Balance = styled.span`font-weight: 700; color: ${p => p.$val <= 0 ? '#ff7c89' : 'rgba(255,255,255,0.6)'};`;
const SentBadge = styled.span`font-size: 0.7rem; font-weight: 700; padding: 2px 9px; border-radius: 20px; background: rgba(46,204,113,0.15); color: #2ecc71; border: 1px solid rgba(46,204,113,0.3);`;
const Empty = styled.div`text-align: center; padding: 40px 0; color: rgba(255,255,255,0.3); font-size: 0.88rem;`;
const ErrMsg = styled.p`color: #ff6b7a; font-size: 0.82rem; margin: 10px 0 0;`;
const OkMsg = styled.p`color: #2ecc71; font-size: 0.82rem; margin: 10px 0 0;`;
const QuillWrap = styled.div`
  .ql-toolbar { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.12); border-radius: 8px 8px 0 0; }
  .ql-container { background: #10101c; border-color: rgba(255,255,255,0.12); border-radius: 0 0 8px 8px; color: white; min-height: 140px; font-size: 0.9rem; }
  .ql-editor.ql-blank::before { color: rgba(255,255,255,0.25); }
`;

export default function PublisherBalanceEmail() {
  const [pubs, setPubs] = useState([]);
  const [pubId, setPubId] = useState('');
  const [cohort, setCohort] = useState('paid_down'); // 'paid_down' | 'renewed'
  const [enabled, setEnabled] = useState(false);
  const [savedEnabled, setSavedEnabled] = useState(false); // the last SAVED enable state — what the server will honour
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [testMsg, setTestMsg] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loadingRecips, setLoadingRecips] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState('');
  const [renewAmount, setRenewAmount] = useState(25);
  const [renewing, setRenewing] = useState(false);
  const [renewMsg, setRenewMsg] = useState('');
  const [err, setErr] = useState('');

  // Load the owner's publications, seed the picker + editor from the chosen one.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/publisher/publications`, { headers: { Authorization: `Bearer ${getToken()}` } });
        const data = await res.json();
        const list = data.publications || [];
        setPubs(list);
        if (list.length && !pubId) setPubId(list[0].id);
      } catch { setErr('Could not load publications'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPub = pubs.find(p => p.id === pubId);

  // When the publication changes, load its saved template (or the default) + recipients.
  useEffect(() => {
    if (!selectedPub) return;
    setEnabled(selectedPub.balance_email_enabled === true);
    setSavedEnabled(selectedPub.balance_email_enabled === true);
    setSubject(selectedPub.balance_email_subject || DEFAULT_SUBJECT);
    setHtml(selectedPub.balance_email_html || DEFAULT_HTML);
    setRenewAmount(Number.isInteger(selectedPub.renewal_default_points) ? selectedPub.renewal_default_points : 25);
    setSaveMsg(''); setTestMsg(''); setSendMsg(''); setRenewMsg(''); setErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubId]);

  const fetchRecipients = useCallback(async () => {
    if (!pubId) return;
    setLoadingRecips(true); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${pubId}/balance-email/recipients?cohort=${cohort}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setRecipients(data.recipients || []);
      // Default to none selected — the admin opts in via checkboxes or "Select all eligible".
      setSelected(new Set());
    } catch (e) { setErr(typeof e.message === 'string' ? e.message : 'Failed to load recipients'); }
    finally { setLoadingRecips(false); }
  }, [pubId, cohort]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const toggleOne = (email) => setSelected(s => {
    const n = new Set(s); n.has(email) ? n.delete(email) : n.add(email); return n;
  });
  const selectAllEligible = () => setSelected(new Set(recipients.filter(r => !r.sent_this_month).map(r => r.email)));
  const clearAll = () => setSelected(new Set());

  const handleSave = async () => {
    setSaving(true); setSaveMsg(''); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${pubId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ balance_email_enabled: enabled, balance_email_subject: subject, balance_email_html: html }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Error ${res.status}`); }
      setSavedEnabled(enabled); // the server now honours this enable state
      setSaveMsg('Template saved');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) { setErr(typeof e.message === 'string' ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTestMsg(''); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${pubId}/balance-email/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ subject, html }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setTestMsg(`Test sent to ${data.sent_to}`);
      setTimeout(() => setTestMsg(''), 4000);
    } catch (e) { setErr(typeof e.message === 'string' ? e.message : 'Test failed'); }
  };

  const handleSend = async () => {
    const emails = [...selected];
    if (!emails.length) return;
    if (!savedEnabled) {
      setErr(enabled
        ? 'You ticked "Enable" but haven\'t saved yet — click "Save template" first, then send.'
        : 'The balance email is turned off. Tick "Enable the balance email for this publication", then "Save template", before sending.');
      return;
    }
    const alreadySent = recipients.filter(r => selected.has(r.email) && r.sent_this_month).length;
    if (alreadySent > 0 && !window.confirm(`${alreadySent} of these were already emailed this month. Send again anyway?`)) return;
    setSending(true); setSendMsg(''); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${pubId}/balance-email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ emails, allow_resend: alreadySent > 0, cohort }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setSendMsg(`Sending to ${data.accepted} subscriber${data.accepted !== 1 ? 's' : ''}… (${data.skipped_already_sent} already sent, ${data.skipped_ineligible} no longer eligible)`);
      // Give the paced background send a moment, then refresh badges.
      setTimeout(fetchRecipients, 4000);
    } catch (e) { setErr(typeof e.message === 'string' ? e.message : 'Send failed'); }
    finally { setSending(false); }
  };

  const renewAmt = Number.isInteger(renewAmount) && renewAmount > 0 ? renewAmount : 25;

  const persistRenewAmount = async () => {
    try {
      await fetch(`${API_URL}/api/publisher/publications/${pubId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ renewal_default_points: renewAmt }),
      });
    } catch { /* non-blocking — the amount still applies to this send */ }
  };

  const doRenew = async (emails, { confirmFirst }) => {
    if (!emails.length) return;
    if (confirmFirst) {
      const alreadyRenewed = recipients.filter(r => emails.includes(r.email) && r.renewed_this_month).length;
      const warn = alreadyRenewed > 0 ? `\n\n${alreadyRenewed} were already renewed this month and will be skipped.` : '';
      if (!window.confirm(`Top up ${emails.length} subscriber${emails.length !== 1 ? 's' : ''} by +${renewAmt} each and mark them renewed? This adds points to their balances.${warn}`)) return;
    }
    setRenewing(true); setRenewMsg(''); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${pubId}/renewals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ emails, amount: renewAmt, allow_rerenew: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setRenewMsg(`Renewed ${data.renewed} (+${data.amount} each)${data.skipped_already_renewed ? `, ${data.skipped_already_renewed} already renewed` : ''}${data.skipped_ineligible ? `, ${data.skipped_ineligible} skipped` : ''}`);
      fetchRecipients();
    } catch (e) { setErr(typeof e.message === 'string' ? e.message : 'Renew failed'); }
    finally { setRenewing(false); }
  };

  const eligibleCount = recipients.filter(r => !r.sent_this_month).length;

  return (
    <Wrap>
      <Title>Monthly balance email</Title>
      <SubTitle>Email subscribers who have paid their points balance down to zero. Sent from reminders.vusub.com.</SubTitle>
      <Hint>ⓘ Best sent around month-end — roughly a week either side. You choose when; nothing sends automatically.</Hint>

      <Row>
        <Select value={pubId} onChange={e => setPubId(e.target.value)}>
          {pubs.length === 0 && <option value="">No publications</option>}
          {pubs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={cohort} onChange={e => setCohort(e.target.value)}>
          <option value="paid_down">Who's at zero now (paid down)</option>
          <option value="renewed">Who I recently renewed</option>
        </Select>
      </Row>
      {cohort === 'renewed' && (
        <Hint>Sending to everyone you topped up &amp; renewed in the last few weeks — use this to send the "you've earned a free month" note after renewing. Works even if the month has ticked over since you renewed them.</Hint>
      )}

      {err && <ErrMsg>{err}</ErrMsg>}

      <Section>
        <SectionTitle>Email template</SectionTitle>
        <Toggle>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Enable the balance email for this publication
        </Toggle>
        <Label>Subject</Label>
        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder={DEFAULT_SUBJECT} />
        <Label>Body</Label>
        <QuillWrap>
          <ReactQuill theme="snow" value={html} onChange={setHtml} modules={QUILL_MODULES} formats={QUILL_FORMATS} />
        </QuillWrap>
        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>
          Merge fields: {'{{subscription_name}}'} and {'{{publisher_name}}'}.
        </div>
        <Row style={{ marginTop: 14, marginBottom: 0 }}>
          <Btn $primary onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save template'}</Btn>
          <SmallBtn onClick={handleTest}>Send test to me</SmallBtn>
          {saveMsg && <OkMsg style={{ margin: 0 }}>{saveMsg}</OkMsg>}
          {testMsg && <OkMsg style={{ margin: 0 }}>{testMsg}</OkMsg>}
        </Row>
      </Section>

      <Section>
        <SectionTitle>{cohort === 'renewed' ? 'Recipients — recently renewed' : 'Recipients — paid down to zero'}</SectionTitle>
        <Row style={{ marginBottom: 12 }}>
          <SmallBtn onClick={selectAllEligible}>Select all eligible ({eligibleCount})</SmallBtn>
          <SmallBtn onClick={clearAll}>Clear</SmallBtn>
          <SmallBtn onClick={fetchRecipients} disabled={loadingRecips}>{loadingRecips ? 'Loading…' : 'Refresh'}</SmallBtn>
          <Toggle as="span" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Renewal top-up:
            <Input as="input" type="number" min={1} max={10000} value={renewAmount}
              onChange={e => setRenewAmount(e.target.value === '' ? '' : Math.max(1, Math.min(10000, parseInt(e.target.value, 10) || 1)))}
              onBlur={persistRenewAmount}
              style={{ width: 70, padding: '6px 8px' }} />
            pts
          </Toggle>
          <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', marginLeft: 'auto' }}>
            {selected.size} selected of {recipients.length}
          </span>
        </Row>
        {loadingRecips ? (
          <Empty>Loading…</Empty>
        ) : recipients.length === 0 ? (
          <Empty>{cohort === 'renewed' ? 'No subscribers have been renewed recently.' : 'No active subscribers are at zero balance right now.'}</Empty>
        ) : (
          <Table>
            <thead>
              <tr><Th></Th><Th>Email</Th><Th>Name</Th><Th>Balance</Th><Th>Email status</Th><Th>Renewal</Th></tr>
            </thead>
            <tbody>
              {recipients.map(r => (
                <tr key={r.email}>
                  <Td><input type="checkbox" checked={selected.has(r.email)} onChange={() => toggleOne(r.email)} /></Td>
                  <Td>{r.email}</Td>
                  <Td style={{ color: 'rgba(255,255,255,0.5)' }}>{r.display_name || '—'}</Td>
                  <Td><Balance $val={r.points_balance}>{r.points_balance}</Balance></Td>
                  <Td>{r.sent_this_month
                    ? <SentBadge>✓ Sent {r.last_sent_at ? new Date(r.last_sent_at).toLocaleDateString() : 'this month'}</SentBadge>
                    : <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem' }}>—</span>}</Td>
                  <Td>{r.renewed_this_month
                    ? <SentBadge>✓ Renewed {r.renewed_at ? new Date(r.renewed_at).toLocaleDateString() : 'this month'}</SentBadge>
                    : <SmallBtn onClick={() => doRenew([r.email], { confirmFirst: false })} disabled={renewing}>Top up &amp; renew +{renewAmt}</SmallBtn>}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <Row style={{ marginTop: 16, marginBottom: 0 }}>
          <Btn $primary onClick={handleSend} disabled={sending || selected.size === 0 || !savedEnabled}>
            {sending ? 'Sending…' : `Send to ${selected.size} selected`}
          </Btn>
          <Btn onClick={() => doRenew([...selected], { confirmFirst: true })} disabled={renewing || selected.size === 0}>
            {renewing ? 'Renewing…' : `Top up all selected (+${renewAmt}) & renew`}
          </Btn>
          {!savedEnabled && (
            <span style={{ fontSize: '0.78rem', color: '#f0a020' }}>
              {enabled
                ? '⚠ Not saved yet — click "Save template" above to turn sending on.'
                : '⚠ Balance email is off — tick "Enable" and "Save template" above before you can send.'}
            </span>
          )}
          {sendMsg && <OkMsg style={{ margin: 0 }}>{sendMsg}</OkMsg>}
          {renewMsg && <OkMsg style={{ margin: 0 }}>{renewMsg}</OkMsg>}
        </Row>
      </Section>
    </Wrap>
  );
}
